/* Clothxtra standalone header */
(() => {
  const lock = (on) => document.documentElement.classList.toggle('cx-scroll-lock', on);

  /* ---------------- Header shell: sticky + sizing ---------------- */
  if (!customElements.get('cx-header')) {
    class CxHeader extends HTMLElement {
      connectedCallback() {
        this.section = this.closest('.shopify-section');
        this.sticky = this.dataset.sticky;
        this.lastY = window.scrollY;

        this.setHeight = this.setHeight.bind(this);
        this.setHeight();
        if ('ResizeObserver' in window) {
          this.ro = new ResizeObserver(this.setHeight);
          this.ro.observe(this);
        }
        window.addEventListener('resize', this.setHeight);

        if (this.sticky === 'on-scroll-up' && this.section) {
          this.onScroll = this.onScroll.bind(this);
          window.addEventListener('scroll', this.onScroll, { passive: true });
        }

        // anchor the customer-account dialog under the account button
        document.addEventListener(
          'open',
          (event) => {
            if (!(event.target instanceof HTMLElement) || !event.target.matches('shopify-account')) return;
            if (!this.contains(event.target)) return;
            const bottom = Math.max(0, Math.round(event.target.getBoundingClientRect().bottom));
            event.target.style.setProperty('--account-dialog-top', `${bottom}px`);
          },
          true
        );
      }

      disconnectedCallback() {
        this.ro?.disconnect();
        window.removeEventListener('resize', this.setHeight);
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      }

      setHeight() {
        document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
      }

      isBusy() {
        return (
          this.classList.contains('cx-menu-open') ||
          this.classList.contains('cx-search-open') ||
          this.classList.contains('cx-drawer-open')
        );
      }

      onScroll() {
        const y = window.scrollY;
        const h = this.offsetHeight;
        if (this.isBusy()) {
          this.lastY = y;
          return;
        }
        if (y > this.lastY && y > h) {
          this.section.classList.add('cx-hdr-hidden');
        } else if (y < this.lastY || y <= h) {
          this.section.classList.remove('cx-hdr-hidden');
        }
        this.lastY = y;
      }

      /** bottom edge of the header in the viewport (px) */
      bottom() {
        return Math.max(0, Math.round(this.getBoundingClientRect().bottom));
      }
    }
    customElements.define('cx-header', CxHeader);
  }

  /* ---------------- Mobile drawer ---------------- */
  if (!customElements.get('cx-drawer')) {
    class CxDrawer extends HTMLElement {
      connectedCallback() {
        this.header = this.closest('cx-header');
        this.toggle = this.header.querySelector(`[aria-controls="${this.id}"]`);
        this.bar = this.header.querySelector('.cx-hdr__bar');
        this.root = this.querySelector('[data-cx-panel-id="root"]');
        this.stack = [this.root];

        this.onKeyUp = (e) => e.key === 'Escape' && this.close(true);
        this.onResize = () => {
          if (window.innerWidth >= 990) this.close(false);
          else this.position();
        };

        this.toggle?.addEventListener('click', () => (this.hidden ? this.open() : this.close(true)));

        this.addEventListener('click', (event) => {
          const go = event.target.closest('[data-cx-go]');
          if (go) {
            event.preventDefault();
            this.push(document.getElementById(go.dataset.cxGo));
            return;
          }
          if (event.target.closest('[data-cx-back]')) {
            event.preventDefault();
            this.pop();
          }
        });
      }

      position() {
        const top = Math.round(this.bar.getBoundingClientRect().bottom);
        this.style.setProperty('--cx-drawer-top', `${top}px`);
      }

      open() {
        this.header.querySelector('cx-search')?.close?.();
        this.hidden = false;
        this.header.classList.add('cx-drawer-open');
        this.position();
        requestAnimationFrame(() => this.classList.add('is-open'));
        this.toggle?.setAttribute('aria-expanded', 'true');
        lock(true);
        document.addEventListener('keyup', this.onKeyUp);
        window.addEventListener('resize', this.onResize);
        this.root.querySelector('.cx-dp__item')?.focus({ preventScroll: true });
      }

      close(returnFocus) {
        if (this.hidden) return;
        this.classList.remove('is-open');
        this.header.classList.remove('cx-drawer-open');
        this.toggle?.setAttribute('aria-expanded', 'false');
        lock(false);
        document.removeEventListener('keyup', this.onKeyUp);
        window.removeEventListener('resize', this.onResize);
        const done = () => {
          this.hidden = true;
          this.reset();
        };
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
        else setTimeout(done, 250);
        if (returnFocus) this.toggle?.focus();
      }

      push(panel) {
        if (!panel) return;
        const current = this.stack[this.stack.length - 1];
        panel.hidden = false;
        panel.scrollTop = 0;
        requestAnimationFrame(() => {
          current.classList.remove('is-active');
          current.classList.add('is-behind');
          panel.classList.add('is-active');
        });
        this.stack.push(panel);
        setTimeout(() => panel.querySelector('[data-cx-back]')?.focus({ preventScroll: true }), 260);
      }

      pop() {
        if (this.stack.length < 2) return;
        const current = this.stack.pop();
        const prev = this.stack[this.stack.length - 1];
        current.classList.remove('is-active');
        prev.classList.remove('is-behind');
        prev.classList.add('is-active');
        setTimeout(() => (current.hidden = true), 260);
      }

      reset() {
        this.querySelectorAll('.cx-dp').forEach((p) => {
          p.classList.remove('is-active', 'is-behind');
          if (p !== this.root) p.hidden = true;
        });
        this.root.classList.add('is-active');
        this.stack = [this.root];
      }
    }
    customElements.define('cx-drawer', CxDrawer);
  }

  /* ---------------- Search ---------------- */
  if (!customElements.get('cx-search')) {
    class CxSearch extends HTMLElement {
      connectedCallback() {
        this.header = this.closest('cx-header');
        this.form = this.querySelector('form');
        this.input = this.querySelector('input[type="search"]');
        this.panel = this.querySelector('[data-cx-search-panel]');
        this.defaultView = this.querySelector('[data-cx-search-default]');
        this.results = this.querySelector('[data-cx-search-results]');
        this.loading = this.querySelector('[data-cx-search-loading]');
        this.clearBtn = this.querySelector('[data-cx-clear]');
        this.url = this.dataset.resultsUrl || window.routes?.predictive_search_url || '/search/suggest';
        this.cache = new Map();
        this.timer = null;

        this.onKeyUp = (e) => e.key === 'Escape' && this.close(true);
        this.onDocClick = (e) => {
          if (!this.contains(e.target)) this.close(false);
        };
        this.onResize = () => this.isOpen && this.position();

        this.input.addEventListener('focus', () => this.open());
        this.input.addEventListener('click', () => this.open());
        this.input.addEventListener('input', () => {
          this.syncClear();
          clearTimeout(this.timer);
          this.timer = setTimeout(() => this.query(), 250);
        });

        this.clearBtn.addEventListener('click', () => {
          this.input.value = '';
          this.syncClear();
          this.showDefault();
          this.input.focus();
        });

        this.querySelector('[data-cx-search-close]').addEventListener('click', () => this.close(false));

        this.form.addEventListener('submit', (event) => {
          if (!this.input.value.trim()) event.preventDefault();
        });

        this.syncClear();
      }

      get isOpen() {
        return this.header.classList.contains('cx-search-open');
      }

      position() {
        this.panel.style.setProperty('--cx-search-top', `${this.header.bottom()}px`);
      }

      open() {
        if (this.isOpen) return;
        this.header.querySelector('cx-drawer')?.close?.(false);
        this.header.querySelector('cx-cascade-menu')?.close?.(false);
        this.header.classList.add('cx-search-open');
        this.panel.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
        // header may have moved (sticky / wrapped rows) — measure after the class lands
        requestAnimationFrame(() => this.position());
        lock(true);
        document.addEventListener('keyup', this.onKeyUp);
        document.addEventListener('click', this.onDocClick);
        window.addEventListener('resize', this.onResize);
        if (this.input.value.trim()) this.query();
      }

      close(returnFocus) {
        if (!this.isOpen) return;
        this.header.classList.remove('cx-search-open');
        this.panel.hidden = true;
        this.input.setAttribute('aria-expanded', 'false');
        lock(false);
        document.removeEventListener('keyup', this.onKeyUp);
        document.removeEventListener('click', this.onDocClick);
        window.removeEventListener('resize', this.onResize);
        this.abort?.abort();
        if (returnFocus) this.input.focus({ preventScroll: true });
        else this.input.blur();
      }

      syncClear() {
        this.clearBtn.hidden = !this.input.value.length;
      }

      showDefault() {
        this.abort?.abort();
        this.loading.hidden = true;
        this.results.innerHTML = '';
        this.results.hidden = true;
        this.defaultView.hidden = false;
      }

      async query() {
        const q = this.input.value.trim();
        if (!q) return this.showDefault();

        if (this.cache.has(q)) return this.render(this.cache.get(q));

        this.abort?.abort();
        this.abort = new AbortController();
        this.loading.hidden = false;

        const params = new URLSearchParams({
          q,
          section_id: 'cx-search-results',
          'resources[type]': 'product,query,collection',
          'resources[limit]': '10',
          'resources[limit_scope]': 'each',
          'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
        });

        try {
          const res = await fetch(`${this.url}?${params.toString()}`, { signal: this.abort.signal });
          if (!res.ok) throw new Error(res.status);
          const text = await res.text();
          const html = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-cx-search-results')?.innerHTML;
          this.cache.set(q, html || '');
          if (this.input.value.trim() === q) this.render(html || '');
        } catch (error) {
          if (error.name !== 'AbortError') {
            this.loading.hidden = true;
            console.error('cx-search', error);
          }
        }
      }

      render(html) {
        this.loading.hidden = true;
        this.defaultView.hidden = true;
        this.results.hidden = false;
        this.results.innerHTML = html;
        this.panel.scrollTop = 0;
      }
    }
    customElements.define('cx-search', CxSearch);
  }
})();


/* Account icon: go straight to the account page (no popup / dropdown) */
(() => {
  const SELECTOR = 'shopify-account, .header__icon--account, .cx-hdr__icon--account, [data-cx-account], .cx-hdr__icon[href*="/account"]';
  document.addEventListener(
    'click',
    (e) => {
      const el = e.target.closest(SELECTOR);
      if (!el) return;
      const href = el.getAttribute('href') || '';
      if (href.includes('logout')) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      window.location.href = root + 'account';
    },
    true
  );
})();




/* Mobile: search box sticks to the top while scrolling, and returns to its place at the top of the page */
(() => {
  const mq = window.matchMedia('(max-width: 989px)');
  let row = null;
  let spacer = null;
  let start = 0;
  let ticking = false;

  function findRow() {
    const hdr = document.querySelector('.cx-hdr') || document.querySelector('header');
    if (!hdr) return null;
    const pinned = hdr.querySelector('[data-cx-sticky-search]');
    if (pinned) return pinned;
    const vw = window.innerWidth;
    const pill = [...hdr.querySelectorAll('form[action*="/search"], [role="search"], input[type="search"], button[class*="search"], a[class*="search"], [class*="search-pill"], [class*="searchbar"]')]
      .filter((el) => el.offsetParent && el.getBoundingClientRect().width > vw * 0.5)
      .sort((a, b) => b.getBoundingClientRect().width - a.getBoundingClientRect().width)[0];
    if (!pill) return null;
    let el = pill;
    const h = pill.getBoundingClientRect().height;
    while (el.parentElement && el.parentElement !== hdr && el.parentElement.getBoundingClientRect().height <= h + 40) {
      el = el.parentElement;
    }
    return el;
  }

  function insideSticky(el) {
    for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
      const pos = getComputedStyle(p).position;
      if (pos === 'sticky' || pos === 'fixed') return true;
    }
    return false;
  }

  function measure() {
    const ref = spacer && spacer.isConnected && row.classList.contains('cx-ss-stuck') ? spacer : row;
    start = ref.getBoundingClientRect().top + window.scrollY;
  }

  function unstick() {
    if (!row) return;
    row.classList.remove('cx-ss-stuck');
    if (spacer) spacer.style.height = '0px';
  }

  function update() {
    ticking = false;
    if (!row || !mq.matches) return unstick();
    const stuck = row.classList.contains('cx-ss-stuck');
    if (window.scrollY > start && !stuck) {
      spacer.style.height = `${row.offsetHeight}px`;
      row.classList.add('cx-ss-stuck');
    } else if (window.scrollY <= start && stuck) {
      unstick();
    }
  }

  function init() {
    unstick();
    row = findRow();
    if (!row || insideSticky(row)) {
      row = null;
      return;
    }
    if (!spacer || !spacer.isConnected) {
      spacer = document.createElement('div');
      spacer.className = 'cx-ss-spacer';
      spacer.setAttribute('aria-hidden', 'true');
    }
    row.before(spacer);
    measure();
    update();
  }

  window.addEventListener('scroll', () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  }, { passive: true });
  window.addEventListener('resize', () => {
    if (!row) return init();
    measure();
    update();
  });
  mq.addEventListener?.('change', init);
  document.addEventListener('shopify:section:load', () => setTimeout(init, 50));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  window.addEventListener('load', () => row && (measure(), update()));
})();